import React, { useState, useEffect, useRef } from 'react';
import { useNetworkInfo } from '../hooks/useNetworkInfo';

/**
 * Lazy loading obrázok s podporou Network Information API a progresívnym načítaním
 * Najprv zobrazí rozmazaný/pixelovaný obrázok, ktorý sa postupne rozkreslí
 */
const LazyImage = ({ 
  src, 
  alt, 
  className = '', 
  placeholder = null,
  lowQualitySrc = null,
  enableProgressive = true, // Povoliť progresívne načítanie s blur efektom
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(false);
  // Začneme s blur len ak je pomalé pripojenie, inak 0
  const [blurIntensity, setBlurIntensity] = useState(0);
  const imgRef = useRef(null);
  const { isSlowConnection, saveData, isSupported } = useNetworkInfo();

  // Nastavíme blur hneď keď sa začne načítavať (pri pomalých pripojeniach)
  useEffect(() => {
    if (shouldLoad && enableProgressive && (isSlowConnection || saveData)) {
      setBlurIntensity(20);
    }
  }, [shouldLoad, enableProgressive, isSlowConnection, saveData]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            // Na pomalých pripojeniach alebo save-data móde môžeme oneskoriť načítanie
            if (isSlowConnection || saveData) {
              // Oneskoríme načítanie o 200ms na pomalých pripojeniach
              setTimeout(() => {
                setShouldLoad(true);
              }, 200);
            } else {
              setShouldLoad(true);
            }
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px' // Začneme načítavať 50px pred tým, ako sa obrázok objaví
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [isSlowConnection, saveData]);

  const handleLoad = () => {
    setIsLoaded(true);
    // Postupne znižujeme blur efekt len pri pomalých pripojeniach
    // Pri rýchlych pripojeniach sa obrázok načíta okamžite bez blur efektu
    const shouldUseProgressive = enableProgressive && (isSlowConnection || saveData);
    
    if (shouldUseProgressive) {
      // Nastavíme počiatočný blur pred začatím animácie
      setBlurIntensity(20);
      const blurSteps = [15, 10, 5, 2, 0];
      let stepIndex = 0;
      const blurInterval = setInterval(() => {
        if (stepIndex < blurSteps.length) {
          setBlurIntensity(blurSteps[stepIndex]);
          stepIndex++;
        } else {
          clearInterval(blurInterval);
        }
      }, 100); // Každých 100ms znížime blur
    } else {
      // Pri rýchlych pripojeniach okamžite odstránime blur
      setBlurIntensity(0);
    }
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true); // Zobrazíme placeholder aj pri chybe
    setBlurIntensity(0);
  };

  // Rozhodnutie, ktorý obrázok načítať
  const imageSrc = shouldLoad 
    ? (isSlowConnection && lowQualitySrc ? lowQualitySrc : src)
    : null;

  // Zistíme, či má obrázok w-auto alebo podobné, čo znamená, že sa má prispôsobiť obsahu
  const hasAutoWidth = className.includes('w-auto') || className.includes('h-auto');
  const wrapperClass = hasAutoWidth 
    ? "relative inline-block" 
    : "relative w-full h-full";

  return (
    <div ref={imgRef} className={wrapperClass}>
      {/* Placeholder alebo loading state */}
      {!isLoaded && !hasError && !imageSrc && (
        <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 animate-pulse flex items-center justify-center z-10">
          {placeholder || (
            <svg className="w-12 h-12 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
            </svg>
          )}
        </div>
      )}

      {/* Placeholder počas načítavania - len ak ešte nie je načítaný a nie je pomalé pripojenie */}
      {imageSrc && !isLoaded && !hasError && !(enableProgressive && (isSlowConnection || saveData)) && (
        <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 animate-pulse z-0"></div>
      )}

      {/* Skutočný obrázok s progresívnym blur efektom len pri pomalých pripojeniach */}
      {imageSrc && (
        <img
          src={imageSrc}
          alt={alt}
          className={`${className} transition-all duration-500 ${
            isLoaded ? 'opacity-100' : (enableProgressive && (isSlowConnection || saveData) ? 'opacity-100' : 'opacity-0')
          }`}
          style={{
            filter: (enableProgressive && (isSlowConnection || saveData)) ? `blur(${blurIntensity}px)` : 'none',
            transform: (enableProgressive && (isSlowConnection || saveData) && blurIntensity > 0) ? 'scale(1.05)' : 'scale(1)',
            transition: 'filter 0.3s ease-out, transform 0.3s ease-out, opacity 0.5s ease-out'
          }}
          onLoad={handleLoad}
          onError={handleError}
          loading="lazy"
          {...props}
        />
      )}

      {/* Error state */}
      {hasError && (
        <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 flex items-center justify-center z-10">
          <div className="text-center text-gray-400">
            <svg className="w-12 h-12 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm">Obrázok sa nepodarilo načítať</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LazyImage;
